import { useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

const PUBSUB_URL = import.meta.env.VITE_PUBSUB_URL as string
const pubSubPost = (channel: string, data: unknown) =>
  fetch(`${PUBSUB_URL}${channel}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ data }),
  }).catch(console.error)
import { gameLuckService } from '../../../services/games'
import type { GameGoldMiner } from '../../../services/games/types'
import { useGetMyProfile } from '../profile/use-get-my-profile'
import { useGlobalStore } from '../../../store/global-store'
import { notifyOpenRecharge } from '../../../commom/rn-bridge'

// A picareta anima pelo menos isto, mesmo que o servidor responda antes —
// sem isso o prêmio "pisca" e parece que nada aconteceu.
const MIN_MINING_MS = 1000
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

// Bloqueios do Jogo Responsável / saldo, em mensagem de gente
const FRIENDLY_PLAY_ERRORS: Record<string, string> = {
  consent_required:
    'Para jogar, assine o termo de Jogo Responsável no menu de jogos.',
  daily_limit_not_set:
    'Defina seu limite diário de perda no menu de jogos para jogar hoje.',
  daily_limit_reached:
    'Você atingiu seu limite de perda de hoje. Os jogos voltam amanhã!',
}

interface Params {
  gameGoldMinerData: GameGoldMiner[]
  timesBet: number
  isModalEarningVisible: boolean
  higherGameGoldMinerData: GameGoldMiner | null
  errorMessage: string | null
  /** saldo depois da última jogada (newCoins) + contador pra a tela reagir sempre */
  newCoins: number | null
  playSeq: number
}

// carteira de jogos ligada: a jogada é paga em Blue Coins (window.GameWallet)
const walletOn = () => !!window.GameWallet?.state.enabled

export const useCreateGoldMiner = () => {
  const { user } = useGetMyProfile()

  const [game, setGame] = useState<Params>({
    gameGoldMinerData: [],
    timesBet: 0,
    isModalEarningVisible: false,
    higherGameGoldMinerData: null,
    errorMessage: null,
    newCoins: null,
    playSeq: 0,
  })

  // sem saldo: com a carteira ligada é aviso "Blue Coins acabaram" (Sim → carteira;
  // Não → sai do jogo); sem ela, a recarga do app como antes
  const handleOpenModalRecharge = () => {
    if (walletOn()) return window.GameWallet.ask('empty')
    notifyOpenRecharge()
  }

  const handleHigherGameLuckNotify = useCallback(
    (gameGoldMinerData: GameGoldMiner) => {
      const message = `${user?.username ?? ''} ganhou ${
        gameGoldMinerData?.giftName ?? ''
      } ${gameGoldMinerData?.quantity ?? ''}x${
        gameGoldMinerData?.valueGift ?? ''
      } na caixa surpresa`

      const colors = {
        default: ['#4568DC80', '#3B478180'],
        'level-up': ['#4568DC80', '#3B478180'],
        earned: ['#4568DC80', '#3B478180'],
        sent: ['#4568DC80', '#3B478180'],
      }

      const messageChat = {
        id: String(Date.now()),
        isInvisible: user?.privacy?.invisibleVisitor ?? false,
        colors: colors,
        message: {
          message,
          username: user?.username,
          giftName: gameGoldMinerData?.giftName,
          imgGift: gameGoldMinerData?.imgGift,
          valueGift: gameGoldMinerData?.valueGift,
          quantity: gameGoldMinerData?.quantity,
          type: 'game-old-miner',
          userId: user?.id,
          userSentId: user?.id,
        },
      }

      pubSubPost('/LIVE-CHAT/br', messageChat)
    },
    [user],
  )

  const handleSuccess = useCallback(
    (data: GameGoldMiner[], timesBet: number) => {
      const notFoundCoins = data.some((result) => {
        return result.status === 'not_found_coins'
      })

      if (notFoundCoins) {
        return handleOpenModalRecharge()
      }

      setGame((prevState) => ({
        ...prevState,
        timesBet,
        gameGoldMinerData: data,
        // 10x com 10 presentes iguais vinha agrupado em 1 linha e o modal nem abria
        isModalEarningVisible: timesBet > 1 || data.length > 1,
      }))

      const checkHigher = data.find((gift) => gift.valueGift >= 520)
      const checkHigherChat = data.find((gift) => gift.valueGift >= 1314)

      if (checkHigherChat) {
        handleHigherGameLuckNotify(checkHigherChat)
      }

      if (checkHigher) {
        // if ('ReactNativeWebView' in window) {
        //   // @ts-ignore
        //   window.ReactNativeWebView.postMessage('earned-high-value-open')
        // }

        setGame((prevState) => ({
          ...prevState,
          higherGameGoldMinerData: checkHigher,
        }))
      }

      // usa o saldo autoritativo do backend (newCoins da ultima jogada) em vez de
      // refetch imediato — o debito no servidor e assincrono (fila SQS) e o refetch
      // retornaria o saldo anterior a jogada
      const lastNewCoins = data[data.length - 1]?.newCoins
      if (typeof lastNewCoins === 'number') {
        if (!walletOn()) useGlobalStore.setState({ coinsInLive: lastNewCoins })
        setGame((prev) => ({ ...prev, newCoins: lastNewCoins, playSeq: prev.playSeq + 1 }))
      }
    },
    [handleHigherGameLuckNotify],
  )

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (params: { timesBet: number }) => {
      const [response] = await Promise.all([
        gameLuckService.createGoldMiner(params),
        sleep(MIN_MINING_MS),
      ])
      return response
    },
    onSuccess: (response, { timesBet }) => {
      // aposta recusada (termo, limite, saldo): antes `data` vinha null e a
      // tela quebrava calada — agora avisa e não mostra prêmio nenhum
      if (response?.error || !Array.isArray(response?.data)) {
        const code = String(response?.error ?? '')
        if (['not_found_coins', 'not_coins', 'insufficient_coins', 'insufficient_balance'].includes(code))
          return handleOpenModalRecharge()
        // termo / limite do Jogo Responsável: o app abre a tela do termo
        if (window.GameWallet?.isConsentError(code)) window.GameWallet.consentAsk(code)
        return setGame((prev) => ({
          ...prev,
          errorMessage:
            FRIENDLY_PLAY_ERRORS[code] ?? 'Falha no servidor. Tente novamente.',
        }))
      }
      handleSuccess(response.data, timesBet)
    },
    onError: () => {
      setGame((prev) => ({
        ...prev,
        errorMessage: 'Falha no servidor. Tente novamente.',
      }))
    },
  })

  // nunca rejeita: o erro já vira mensagem na tela (onError/onSuccess)
  const create = useCallback(
    (params: { timesBet: number }) => {
      setGame((prev) => ({ ...prev, errorMessage: null }))
      return mutateAsync(params).catch(() => undefined)
    },
    [mutateAsync],
  )

  return { ...game, isLoading: isPending, create, setGame }
}
